import { useLayoutEffect, useRef, type ChangeEvent, type ReactNode } from 'react';
import { getDisplayLocale } from '@nest-recruit/shared';
import { currencySymbol } from '../lib/currency-symbol';
import { Input } from './Input';

/**
 * A whole amount of money, typed: the currency's symbol before it, its period after ("a month"),
 * and the digits grouped as they are typed ("68,000", or "1,00,000" where the organisation reads
 * Indian grouping). Only digits get in, so the value handed back is always a plain digit string
 * ("" when empty) for the caller to turn into minor units (#7). The caret stays beside the digit
 * it was beside when a comma appears or goes.
 */

/** 999,999,999: the largest whole amount the money fields accept. */
const DEFAULT_MAX_DIGITS = 9;

interface MoneyInputProps {
  /** Digits only, "" when empty. */
  readonly value: string;
  readonly onChange: (digits: string) => void;
  /** An ISO code ("BDT"); shown as its symbol. */
  readonly currency: string;
  /** "a month". */
  readonly suffix?: string;
  readonly id?: string;
  readonly label?: string;
  readonly describedBy?: string;
  readonly isInvalid?: boolean;
  readonly disabled?: boolean;
  readonly maxDigits?: number;
}

/** "085000" → "85000"; anything but a digit dropped. */
export function digitsOf(text: string, maxDigits: number = DEFAULT_MAX_DIGITS): string {
  return text.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, maxDigits);
}

/** "68000" → "68,000" in the organisation's grouping. */
export function groupDigits(digits: string): string {
  if (digits === '') return '';
  return new Intl.NumberFormat(getDisplayLocale(), { maximumFractionDigits: 0 }).format(BigInt(digits));
}

/** Where the caret goes in `formatted` so that `digitsBefore` digits sit to its left. */
function caretAfterDigits(formatted: string, digitsBefore: number): number {
  if (digitsBefore <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted.charAt(index))) seen += 1;
    if (seen === digitsBefore) return index + 1;
  }
  return formatted.length;
}

export function MoneyInput({
  value,
  onChange,
  currency,
  suffix,
  id,
  label,
  describedBy,
  isInvalid = false,
  disabled = false,
  maxDigits = DEFAULT_MAX_DIGITS,
}: MoneyInputProps): ReactNode {
  const input = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const formatted = groupDigits(value);

  useLayoutEffect(() => {
    const digitsBefore = pendingCaret.current;
    if (digitsBefore === null || !input.current || document.activeElement !== input.current) return;
    pendingCaret.current = null;
    const caret = caretAfterDigits(input.current.value, digitsBefore);
    input.current.setSelectionRange(caret, caret);
  });

  function change(event: ChangeEvent<HTMLInputElement>): void {
    const typed = event.target.value;
    const caret = event.target.selectionStart ?? typed.length;
    pendingCaret.current = digitsOf(typed.slice(0, caret), maxDigits).length;
    onChange(digitsOf(typed, maxDigits));
  }

  return (
    <Input
      ref={input}
      id={id}
      aria-label={label}
      aria-describedby={describedBy}
      isInvalid={isInvalid}
      disabled={disabled}
      isNumeric
      inputMode="numeric"
      autoComplete="off"
      prefix={currencySymbol(currency)}
      suffix={suffix}
      value={formatted}
      onChange={change}
    />
  );
}
